import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import {
  Alert,
  AlertService,
  AlertType,
  EmptyStateModule,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig,
  TableModule
} from '@michelin/theme';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { map, Observable } from 'rxjs';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { Store } from '@ngxs/store';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { ColumnPreferencesService } from '../../../../shared/services/column-preferences.service';
import { ColumnPreferencesHelperService } from '../../../../shared/services/column-preferences-helper.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedModule } from '../../../../shared/shared.module';
import { CasingCreditDetails } from '../../models/casing-credit-details.model';
import { PaginatedCasingCreditDetails } from '../../models/response.model';
import { hasRolePermission } from '../../../user-management/utils/UserSetupUtils';
import { ROLES } from '../../../authentication/authRoles.constants';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';
import { DealerState } from '../../../../shared/state/DealerState';
import { MatDialog } from '@angular/material/dialog';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';

@Component({
  selector: 'app-casing-credit-details',
  templateUrl: './casing-credit-details.component.html',
  styleUrl: './casing-credit-details.component.scss',
  imports: [SharedModule, TableModule, EmptyStateModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CasingCreditDetailsComponent implements OnInit {
  @Input() casingCreditId: string;
  code: string;
  description: string;
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<CasingCreditDetails>;
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public router = inject(Router);
  public alertService = inject(AlertService);
  public translationService = inject(TranslationService);
  public cdr = inject(ChangeDetectorRef);
  public route = inject(ActivatedRoute);
  public columnPrefService = inject(ColumnPreferencesService);
  private dialog = inject(MatDialog);
  public columnPrefsHelper = inject(ColumnPreferencesHelperService);

  initialFormValues: any = {};
  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  casingCreditDetails: CasingCreditDetails[] = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  pagination: any;
  pageSizeOptions = [10, 25, 50, 100];
  currentPage = 1;
  pageSize = 10;
  productSaveDisabled = true;
  isCreateInProgress = false;
  saveEnabled = false;

  sizeList: string[] = [];
  descriptionList: string[] = [];
  baseCreditList: string[] = [];
  repairGridList: string[] = [];
  // Add a map to store all modified rows from all pages
  allModifiedRows: Map<string, any> = new Map();
  dealerName = this.store.selectSnapshot(DealerState.currentDealer).name;

  openExportModal(): void {
    const columns: ExportColumnDef[] = Object.entries(this.columnNames).map(([key, label]) => ({ key, label, selected: true }));
    const data: ExportModalData = {
      columns,
      dataFetcher: this.exportDataFetcher,
      fileNamePrefix: this.dealerName + '-CasingCreditDetails-export',
      extraColumns: this.extraColumns,
      maxExportRows: 10000
    };
    this.dialog.open(ExportModalComponent, { data, width: '480px' });
  }

  tableConfig: TableConfig<any> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    columns: {}
  };

  columnNames: Record<string, string> = {};
  private canonicalColumns: Record<string, any> = {};
  columnKeys: string[] = [];
  columnOrder: string[] = [];
  columnVisibility: Record<string, boolean> = {};

  exportDataFetcher: () => Observable<ExportResponse> = () =>
    this.productDataService.getCasingCreditDetails(this.filterFormBackend.value, this.casingCreditId, 1, 10000).pipe(
      map((response: any) => ({
        data: (response.data.casingCreditDetails as CasingCreditDetails[]).map((item) => ({
          size: item.size,
          description: item.description,
          baseCredit: item.baseCredit,
          repairGrid: item.repairGrid
        }))
      }))
    );

  fetcher: PaginatorFetcher<any> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;

    // Store changes from current page before fetching new data
    const modifiedRows = this.getModifiedRows();
    modifiedRows.forEach((row) => {
      this.allModifiedRows.set(row.casingCreditDetailId, row);
    });

    return this.productDataService
      .getCasingCreditDetails({ ...this.filterFormBackend.value, ...filters }, this.casingCreditId, filters.page, filters.limit)
      .pipe(
        map((response: any) => {
          this.casingCreditDetails = response.data.casingCreditDetails || [];
          this.pagination = response.data.pagination;
          this.updateDisplayedRowsForm(this.casingCreditDetails);
          return { items: this.casingCreditDetails, count: this.pagination.totalRecords };
        })
      );
  };

  constructor() {
    this.filterForm = this.fb.group({
      size: [[]],
      description: [[]],
      baseCredit: [[]],
      repairGrid: [[]]
    });
    this.filterFormBackend = this.fb.group({
      size: [[]],
      description: [[]],
      baseCredit: [[]],
      repairGrid: [[]]
    });
  }

  ngOnInit(): void {
    this.casingCreditId = this.route.snapshot.paramMap.get('casingCreditId');
    const { code, description } = history.state;
    this.code = code;
    this.description = description;
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.saveEnabled = !this.productSaveDisabled && hasRolePermission(ROLES.edit_product);
    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000
      } as Alert);
    }
    this.productDataService.getCasingCreditDetailsSearchFilters(this.casingCreditId).subscribe((response: any) => {
      this.sizeList = response.data.sizes || [];
      this.descriptionList = response.data.descriptions || [];
      this.baseCreditList = response.data.baseCredits || [];
      this.repairGridList = response.data.repairGrids || [];
    });
    this.filterForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(800)).subscribe(() => {
      this.generateFilterForm();
      this.tableRef?.patchExtraFilters(this.filterFormBackend.value, false);
    });
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      // canonical and display names for columns
      this.canonicalColumns = {
        casingSize: { cell: (row) => row.size, header: this.translationService.translate('casingSizeText') },
        description: { cell: (row) => row.description, header: this.translationService.translate('descriptionText') },
        baseCredit: { cell: 'baseCredit', header: this.translationService.translate('baseCreditText') },
        repairGrid: { cell: 'repairGridDropdown', header: this.translationService.translate('repairGridText') }
      };

      // columnNames map keys to human labels used for export/selector
      this.columnNames = {
        casingSize: this.translationService.translate('casingSizeText'),
        description: this.translationService.translate('descriptionText'),
        baseCredit: this.translationService.translate('baseCreditText'),
        repairGrid: this.translationService.translate('repairGridText')
      };

      // initialize columns with user preferences (localStorage)
      const init = this.columnPrefsHelper.initialize('casing-credit-details', this.canonicalColumns);
      this.tableConfig.columns = init.tableColumns;
      this.columnNames = init.columnNames;
      this.columnKeys = init.columnKeys;
      this.columnOrder = init.columnOrder;
      this.columnVisibility = init.columnVisibility;
      this.cdr.markForCheck();
    });
  }

  // Toggle visibility for a column and persist preferences (delegates to helper)
  toggleColumn(key: string): void {
    const result = this.columnPrefsHelper.toggleAndSave(
      'casing-credit-details',
      key,
      this.canonicalColumns,
      this.columnOrder,
      this.columnVisibility
    );
    this.tableConfig.columns = result.tableColumns;
    this.columnVisibility = result.columnVisibility;
    this.cdr.markForCheck();
  }

  /**
   * Gets the rows with modified amount values
   * @returns Array of modified rows with their new amount values
   */
  getModifiedRows(): any[] {
    if (!this.initialFormValues || !this.displayedRowsForm) {
      return [];
    }

    const currentValues = this.displayedRowsForm.getRawValue();
    const modifiedRows = [];

    if (currentValues.rows && this.initialFormValues.rows) {
      for (let i = 0; i < currentValues.rows.length; i++) {
        if (i < this.initialFormValues.rows.length) {
          // Only check existing rows
          if (
            currentValues.rows[i].baseCredit !== this.initialFormValues.rows[i].baseCredit ||
            currentValues.rows[i].repairGrid !== this.initialFormValues.rows[i].repairGrid
          ) {
            // Add the modified row with its new amount value
            modifiedRows.push({
              casingCreditDetailId: currentValues.rows[i].casingCreditDetailId,
              size: currentValues.rows[i].size,
              description: currentValues.rows[i].description,
              baseCredit: currentValues.rows[i].baseCredit,
              repairGrid: currentValues.rows[i].repairGrid
            });
          }
        }
      }
    }
    return modifiedRows;
  }

  updateDisplayedRowsForm(rows: any[]): void {
    const rowForms = rows.map((row) => {
      // If this row was modified previously, use the modified value
      const modifiedRow = this.allModifiedRows.get(row.casingCreditDetailId);
      return this.fb.group({
        size: [row.size],
        description: [row.description],
        baseCredit: [modifiedRow ? modifiedRow.baseCredit : row.baseCredit],
        repairGrid: [modifiedRow ? modifiedRow.repairGrid : row.repairGrid],
        casingCreditDetailId: [row.casingCreditDetailId]
      });
    });
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
    // Store initial form values to track changes
    this.initialFormValues = JSON.parse(JSON.stringify(this.displayedRowsForm.getRawValue()));
  }

  getControl(item: CasingCreditDetails, controlName: string): FormControl {
    const index = this.casingCreditDetails.findIndex((row) => row.casingCreditDetailId === item.casingCreditDetailId); // Use appropriate unique key
    if (index === -1) {
      return new FormControl('');
    }
    return (this.displayedRowsForm.get('rows') as FormArray).at(index).get(controlName) as FormControl;
  }

  clearFilters(): void {
    this.filterForm.reset();
  }

  cancel(): void {
    this.router.navigate(['/home/productData/casingCredit']);
  }

  save(): void {
    if (!this.saveEnabled) {
      return;
    }

    // Store changes from current page first
    const modifiedRows = this.getModifiedRows();
    modifiedRows.forEach((row) => {
      this.allModifiedRows.set(row.casingCreditDetailId, row);
    });

    // Convert Map to array for API submission
    const allChanges = Array.from(this.allModifiedRows.values());

    if (allChanges.length === 0) {
      // No changes to save
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('noChangesToSaveText')
      } as Alert);
      return;
    }

    this.isCreateInProgress = true;
    if (allChanges.length > 0) {
      const payload: PaginatedCasingCreditDetails = {
        casingCreditDetails: allChanges,
        pagination: this.pagination
      };
      this.productDataService.updateCasingCreditDetails(payload).subscribe({
        next: () => {
          // Clear the modifications map after successful save
          this.allModifiedRows.clear();
          // Reset the form tracking to prevent unsaved changes popup
          this.initialFormValues = JSON.parse(JSON.stringify(this.displayedRowsForm.getRawValue()));

          this.alertService.showAlert({
            type: AlertType.success,
            content: this.translationService.translate('camSaveSuccessText')
          } as Alert);
          this.isCreateInProgress = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.alertService.showAlert({
            type: AlertType.critical,
            content: this.translationService.translate('criticalErrorAlertText')
          } as Alert);
          this.isCreateInProgress = false;
          this.cdr.markForCheck();
        }
      });
    } else {
      this.isCreateInProgress = false;
      this.cdr.markForCheck();
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('noModifiedText')
      } as Alert);
    }
  }

  generateFilterForm(): void {
    const filterSize = this.filterForm.get('size').value;
    const filterDescription = this.filterForm.get('description').value;
    const filterBaseCredit = this.filterForm.get('baseCredit').value;
    const filterRepairGrid = this.filterForm.get('repairGrid').value;
    this.filterFormBackend.patchValue({
      size: filterSize || '',
      description: filterDescription || '',
      baseCredit: filterBaseCredit || '',
      repairGrid: filterRepairGrid || ''
    });
  }

  /**
   * Called on keydown to mark deletion intent for environments where inputType is not available.
   */
  handleKeydown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }
    if (event.key === 'Backspace' || event.key === 'Delete') {
      input.dataset.isDeleting = 'true';
    }
  }

  /**
   * Restrict input to at most two digits after decimal point.
   * Allows empty string so user can delete all characters (no forced '0').
   * Preserves caret position and keeps FormControl in sync.
   */
  restrictTwoDecimalPlaces(event: Event, item: CasingCreditDetails): void {
    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }

    const inputEventType = (event as any)?.inputType ?? '';
    const isDeleting = input.dataset.isDeleting === 'true' || inputEventType.startsWith('delete');

    const originalValue = input.value ?? '';
    const selectionStart = input.selectionStart ?? originalValue.length;

    const cleanValue = (val: string) => {
      if (val === '') {
        return '';
      }
      let v = val.replace(/[^0-9.]/g, ''); // remove invalid chars
      v = v.replace(/(\..*)\./g, '$1'); // keep only first dot
      if (v === '.') {
        return '0.';
      }
      if (v.startsWith('.')) {
        v = '0' + v;
      }
      if (v.includes('.')) {
        const [intPart, fracPart] = v.split('.');
        v = intPart + '.' + (fracPart ? fracPart.slice(0, 2) : '');
      } else {
        // remove leading zeros only if followed by another digit (so "0" stays "0")
        v = v.replace(/^0+(\d)/, '$1');
        // do NOT force v = '0' if result becomes empty — allow empty
      }
      return v;
    };

    let newValue = cleanValue(originalValue);

    // If deletion intent and the cleaned value is '0', allow it to become empty so backspace can clear.
    if (isDeleting && newValue === '0') {
      newValue = '';
    }

    const originalPrefix = originalValue.slice(0, selectionStart);
    let cleanedPrefix = cleanValue(originalPrefix);
    if (isDeleting && cleanedPrefix === '0') {
      cleanedPrefix = '';
    }

    // Clear deletion flag so it doesn't affect next non-deleting input
    if (input.dataset.isDeleting) {
      delete input.dataset.isDeleting;
    }

    if (newValue === originalValue) {
      try {
        // ignore if nothing to sync
      } catch {
        // ignore
      }
      return;
    }

    const newCaretPos = Math.min(cleanedPrefix.length, newValue.length);
    input.value = newValue;
    try {
      input.setSelectionRange(newCaretPos, newCaretPos);
    } catch {
      // ignore
    }

    try {
      const control = this.getControl(item, 'baseCredit');
      if (control) {
        control.setValue(newValue === '' ? null : newValue, { emitEvent: false });
      }
    } catch {
      // ignore
    }

    this.cdr.markForCheck();
  }

  get extraColumns(): Record<string, string> {
    return {
      Code: this.code ?? ''
    };
  }
}
